import { BalanceData } from "../data/balance";
import { AchievementDef, AchievementReward, AchievementsData } from "../data/achievements";
import { chestOfRarity, chestShare, grantReward, metaSeed } from "./orders";
import { PaidReward, RewardTarget, payForge } from "./events";

/**
 * Achievements (FINISH_PLAN WP7, M19, DESIGN §9.7): long-term goals over lifetime metrics. An achievement is done the
 * first time its metric reaches its target — and stays done (a metric that can fall, like gear owned, never un-earns
 * it) — then its reward is claimed once from the Achievements sheet. Pure and deterministic: the app gathers the
 * metrics (app achievements.ts) and gives the time; the chest a reward holds is seeded from the player and the id, so
 * a retried claim is the same chest.
 */

/** The lifetime metrics an achievement can watch (gen.py ACHIEVEMENT_METRICS keeps the data to these). */
export const ACHIEVEMENT_METRICS = [
  "levelsCleared", "stars", "threeStarLevels", "perfectChapters", "bossesDefeated",
  "kills", "airKills", "eliteKills", "wins", "noLeakWins",
  "towerLevel", "baseLevel", "towersOwned", "builders", "towersMastered",
  "bestRarity", "itemLevel", "mythics", "heroicStars", "trialWins",
  // Iron challenges and Endless mode (docs/MODES.md): Iron stars, and the best Endless run's waves (all time)
  "ironStars", "endlessBest",
  // Gear v2 (docs/GEAR_V2.md): fusions made, the best rarity a fusion made, item levels raised with essence
  "fusions", "fusedRarity", "raisedLevels",
  // M36 rare creatures (docs/RARES.md): kinds seen, kinds killed
  "raresSeen", "raresKilled",
  // M40 the Starfall Wyrm (docs/RARES.md): battles it appeared in, times it was slain
  "wyrmsSeen", "wyrmsKilled",
] as const;
export type AchievementMetric = (typeof ACHIEVEMENT_METRICS)[number];

/** Every metric's current value. */
export type AchievementFacts = Readonly<Record<AchievementMetric, number>>;

/** All metrics at zero (a fresh save; tests start here). */
export function zeroFacts(): Record<AchievementMetric, number> {
  return Object.fromEntries(ACHIEVEMENT_METRICS.map((m) => [m, 0])) as Record<AchievementMetric, number>;
}

/** How far along an achievement is: the metric's value capped at the target. */
export function progressOf(def: AchievementDef, facts: AchievementFacts): { readonly have: number; readonly target: number } {
  const v = facts[def.metric as AchievementMetric] ?? 0;
  return { have: Math.min(Math.max(0, v), def.target), target: def.target };
}

/** A paid claim: what the reward paid (the chest rolled, for the caller to bank) and the title it unlocked. */
export interface AchievementClaim { readonly def: AchievementDef; readonly paid: PaidReward; readonly title: string }

export interface AchievementsJson {
  v: number;
  /** Achievement id → when it was done (unix seconds). */
  done: Record<string, number>;
  claimed: string[];
  /** Done achievements the player has been told about (the hub's banner). */
  seen: string[];
  /** The commander title worn ("" for none). */
  title: string;
  /** Titles another feature granted (the season pass, MP-22); absent when none. */
  granted?: string[];
}

export const ACHIEVEMENTS_VERSION = 1;
const ID = /^[A-Za-z0-9_]{1,48}$/;
/** A title's longest text. */
const TITLE_MAX = 64;

/** The seed of an achievement reward's chest. */
export function achievementSeed(playerId: string, id: string): bigint { return metaSeed(playerId, `achievement:${id}`, 0); }

/** Pays an achievement's reward (as payReward pays an event's, logged as "achievement" in the wallet). */
export function payAchievement(balance: BalanceData, reward: AchievementReward, t: RewardTarget): PaidReward {
  const level = Math.max(1, t.level);
  const gold = reward.resources.isZero() ? 0 : chestShare(balance, level, reward.resources);
  grantReward(t.profile, { gold, speedUpMinutes: reward.speedUpMinutes });
  t.wallet.grantGems(reward.gems, "achievement", t.at, t.ref);
  t.wallet.grant("marks", reward.marks, "achievement", t.at, t.ref);
  payForge(t.profile, reward);
  const chest = reward.chest === null ? null : chestOfRarity(balance, level, reward.chest, t.seed);
  return { gold, speedUpMinutes: reward.speedUpMinutes, gems: reward.gems, marks: reward.marks, chest, emblem: "", essence: reward.essence, sigil: reward.sigil, sigils: reward.sigils };
}

export class AchievementProgress {
  private readonly done = new Map<string, number>();
  private readonly claimed = new Set<string>();
  private readonly seen = new Set<string>();
  /** The commander title worn: one an achievement unlocked and the player picked. */
  title = "";
  /** Titles granted outside the achievements (the season pass's, MP-22), in the order they came. */
  private readonly granted: string[] = [];

  isDone(id: string): boolean { return this.done.has(id); }
  doneAt(id: string): number | null { return this.done.get(id) ?? null; }
  isClaimed(id: string): boolean { return this.claimed.has(id); }
  get doneCount(): number { return this.done.size; }

  /** Marks every achievement whose metric reached its target done (sticky); returns the newly done ones, in data order. */
  sync(data: AchievementsData, facts: AchievementFacts, now: number): AchievementDef[] {
    const fresh: AchievementDef[] = [];
    for (const d of data.list) {
      if (this.done.has(d.id)) continue;
      const p = progressOf(d, facts);
      if (p.have >= p.target) { this.done.set(d.id, now); fresh.push(d); }
    }
    return fresh;
  }

  /** Done and not yet claimed, in data order (the More row's red count). */
  claimable(data: AchievementsData): AchievementDef[] { return data.list.filter((d) => this.done.has(d.id) && !this.claimed.has(d.id)); }
  /** Done and not yet announced (the hub's banner), in data order. */
  unseen(data: AchievementsData): AchievementDef[] { return data.list.filter((d) => this.done.has(d.id) && !this.seen.has(d.id)); }
  markSeen(ids: readonly string[]): void { for (const id of ids) if (this.done.has(id)) this.seen.add(id); }

  /**
   * Claims a done achievement once: pays its reward and returns what it paid, or null (paying nothing) when it is not
   * done or already claimed. The caller banks the chest and saves.
   */
  claim(balance: BalanceData, def: AchievementDef, target: RewardTarget): AchievementClaim | null {
    if (!this.done.has(def.id) || this.claimed.has(def.id)) return null;
    this.claimed.add(def.id);
    this.seen.add(def.id);
    const paid = payAchievement(balance, def.reward, target);
    return { def, paid, title: def.reward.title };
  }

  /** Titles unlocked by claimed achievements, in data order. */
  titles(data: AchievementsData): string[] {
    const own = data.list.filter((d) => d.reward.title !== "" && this.claimed.has(d.id)).map((d) => d.reward.title);
    return [...own, ...this.granted.filter((t) => !own.includes(t))];
  }

  /** Grants a title from outside the achievements (the season pass); false when it was already granted. */
  grantTitle(title: string): boolean {
    if (title === "" || title.length > TITLE_MAX || this.granted.includes(title)) return false;
    this.granted.push(title);
    return true;
  }

  /** Wears an unlocked title ("" takes it off); false for a title not unlocked. */
  wearTitle(data: AchievementsData, title: string): boolean {
    if (title !== "" && !this.titles(data).includes(title)) return false;
    this.title = title;
    return true;
  }

  toJson(): AchievementsJson {
    const j: AchievementsJson = { v: ACHIEVEMENTS_VERSION, done: Object.fromEntries(this.done), claimed: [...this.claimed], seen: [...this.seen], title: this.title };
    if (this.granted.length > 0) j.granted = [...this.granted];
    return j;
  }

  /** Restores progress: malformed entries are dropped; a claim or a sighting of something not done is ignored. */
  static fromJson(j: unknown): AchievementProgress {
    const p = new AchievementProgress();
    if (typeof j !== "object" || j === null || Array.isArray(j)) return p;
    const src = j as Partial<AchievementsJson>;
    if (typeof src.done === "object" && src.done !== null && !Array.isArray(src.done)) {
      for (const [id, at] of Object.entries(src.done)) if (ID.test(id) && Number.isFinite(at)) p.done.set(id, at);
    }
    const ids = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && p.done.has(x)) : []);
    for (const id of ids(src.claimed)) p.claimed.add(id);
    for (const id of ids(src.seen)) p.seen.add(id);
    if (typeof src.title === "string" && src.title.length <= TITLE_MAX) p.title = src.title;
    if (Array.isArray(src.granted)) for (const t of src.granted) if (typeof t === "string") p.grantTitle(t);
    return p;
  }
}
